import React, { useState, useEffect } from 'react';
import { Star, Send } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Reviews = () => {
  const [reviews, setReviews] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ name: '', review_text: '', rating: 5 });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/reviews`);
      setReviews(response.data);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');
    try {
      await axios.post(`${BACKEND_URL}/api/reviews`, formData);
      setSubmitMessage('Thank you for your review!');
      setFormData({ name: '', review_text: '', rating: 5 });
      setShowForm(false);
      fetchReviews();
    } catch (error) {
      setSubmitMessage('Failed to submit review. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const StarRating = ({ rating, interactive, onRate }) => (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          size={interactive ? 24 : 16}
          className={`${
            star <= rating ? 'text-[#FF8C42] fill-[#FF8C42]' : 'text-white/20'
          } ${interactive ? 'cursor-pointer hover:text-[#FF8C42] transition-colors' : ''}`}
          onClick={() => interactive && onRate && onRate(star)}
          data-testid={interactive ? `star-rating-${star}` : undefined}
        />
      ))}
    </div>
  );

  return (
    <section id="reviews" className="py-24 px-6 bg-[#111113]" data-testid="reviews-section">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <p className="text-[#FF8C42] text-sm tracking-[0.3em] uppercase mb-4">Testimonials</p>
          <h2
            className="text-4xl md:text-5xl font-serif text-white mb-6"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Client Reviews
          </h2>
          <p className="text-white/70 text-lg max-w-2xl mx-auto">
            Hear what our clients have to say about their experience with Classic Decor
          </p>
        </div>

        {/* Reviews Grid */}
        {reviews.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {reviews.map((review) => (
              <div
                key={review.id}
                className="bg-[#1a1a1c] p-8 rounded-sm border border-white/5 hover:border-[#FF8C42]/20 transition-all duration-300"
                data-testid="review-card"
              >
                <StarRating rating={review.rating} />
                <p className="text-white/70 mt-4 mb-6 leading-relaxed text-sm italic">
                  "{review.review_text}"
                </p>
                <div className="border-t border-white/10 pt-4">
                  <p className="text-white font-medium">{review.name}</p>
                  <p className="text-white/40 text-xs mt-1">
                    {new Date(review.created_at).toLocaleDateString('en-IN', {
                      year: 'numeric', month: 'long', day: 'numeric'
                    })}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}

        {reviews.length === 0 && !showForm && (
          <div className="text-center mb-12">
            <p className="text-white/50 text-lg">Be the first to share your experience with Classic Decor!</p>
          </div>
        )}

        {/* Submit Message */}
        {submitMessage && (
          <div className="text-center mb-6">
            <p className={`text-lg ${submitMessage.includes('Thank') ? 'text-green-400' : 'text-red-400'}`}>
              {submitMessage}
            </p>
          </div>
        )}

        {/* Toggle / Form */}
        <div className="text-center">
          {!showForm ? (
            <button
              onClick={() => setShowForm(true)}
              className="bg-[#FF8C42] hover:bg-[#ff9d5c] text-white px-8 py-4 rounded-sm font-medium tracking-wide transition-all duration-300 hover:shadow-lg hover:shadow-[#FF8C42]/30"
              data-testid="write-review-btn"
            >
              Write a Review
            </button>
          ) : (
            <div className="max-w-xl mx-auto bg-[#1a1a1c] p-8 rounded-sm">
              <h3 className="text-xl font-serif text-white mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
                Share Your Experience
              </h3>
              <form onSubmit={handleSubmit} className="space-y-5 text-left">
                <div>
                  <label className="block text-white/80 mb-2 text-sm">Your Name</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="w-full px-4 py-3 bg-[#0a0a0c] border border-white/10 rounded-sm text-white focus:border-[#FF8C42] focus:outline-none focus:ring-1 focus:ring-[#FF8C42] transition-colors"
                    placeholder="Your name"
                    data-testid="review-name-input"
                  />
                </div>
                <div>
                  <label className="block text-white/80 mb-2 text-sm">Rating</label>
                  <StarRating
                    rating={formData.rating}
                    interactive
                    onRate={(r) => setFormData({ ...formData, rating: r })}
                  />
                </div>
                <div>
                  <label className="block text-white/80 mb-2 text-sm">Your Review</label>
                  <textarea
                    value={formData.review_text}
                    onChange={(e) => setFormData({ ...formData, review_text: e.target.value })}
                    required
                    rows={4}
                    className="w-full px-4 py-3 bg-[#0a0a0c] border border-white/10 rounded-sm text-white focus:border-[#FF8C42] focus:outline-none focus:ring-1 focus:ring-[#FF8C42] transition-colors resize-none"
                    placeholder="Tell us about your experience..."
                    data-testid="review-text-input"
                  />
                </div>
                <div className="flex gap-4">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex-1 bg-[#FF8C42] hover:bg-[#ff9d5c] text-white px-6 py-3 rounded-sm font-medium tracking-wide transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50"
                    data-testid="submit-review-btn"
                  >
                    <span>{isSubmitting ? 'Submitting...' : 'Submit Review'}</span>
                    <Send size={16} />
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowForm(false)}
                    className="px-6 py-3 border border-white/20 text-white/70 rounded-sm hover:border-white/40 transition-colors"
                    data-testid="cancel-review-btn"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default Reviews;
